import {
    AfterViewInit,
    Component,
    ElementRef,
    Inject,
    NgZone,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
    ApplicationFacade,
    CustomerFacade,
    Departure,
    DetailedRoute,
    FavoriteLocation,
    FavoritesFacade,
    MapCoordinates,
    MapIcons,
    OpenTripPlannerFacade,
    OpenTripPlannerService,
    RealtimeFacade,
    RealtimeObj,
    Route,
    Stop,
    StopMarker,
    TripDetails
} from '@ridango/components';
import {
    AuthService,
    BaseComponent,
    LatLng as RidLatLng,
    MapFitService,
    MapPoint,
    NetworkService,
    RouteDataService
} from '@ridango/core';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    switchMap,
    take,
    takeUntil
} from 'rxjs/operators';
import { LatLng, PopupEvent } from 'leaflet';
import Env from '@ridango/core/env';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
    IconSizeEnum,
    IconSizeOrderEnum,
    IconUtils,
    MapApiService,
    MapIconConfigs,
    MarkerItem,
    MarkerType,
    SmallScreenSheetModalContainerComponent,
    SmallScreenSheetModalContainerService
} from '@ridango/widgets';
import { BehaviorSubject, combineLatest, of, timer } from 'rxjs';
import { getDistance } from 'geolib';
import { HeaderMenuService } from '../../shared/services/header-menu.service';
import { BannersFacade } from '@ridango/components/lib/banners';
import { ObjectUtil } from '@ridango/core/lib/utils/object-util';
import { Capacitor } from '@capacitor/core';

@Component({
    templateUrl: './explore-page.component.html',
    styleUrls: ['./explore-page.component.scss']
})
export class ExplorePageComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('panel') panel: SmallScreenSheetModalContainerComponent;
    @ViewChild('bannersContainer') bannersContainer?: ElementRef<HTMLElement>;

    readonly NEARBY_RADIUS_METERS = 2000;
    readonly MarkerType = MarkerType;
    readonly IconSizeEnum = IconSizeEnum;
    readonly IconSizeOrderEnum = IconSizeOrderEnum;
    readonly stopsVisibleFromZoomSize: IconSizeEnum =
        this.env.MAP.STOPS_VISIBLE_FROM_ZOOM_SIZE ?? this.IconSizeEnum.small;

    mapCenter: MapCoordinates = this.env.MAP.COORDINATES;
    iconStop = MapIcons('stop', this.env.THEME_NAME);
    iconStopHighlighted = MapIcons('stopHighlighted', this.env.THEME_NAME);
    circleHuman = MapIcons('circleHuman', this.env.THEME_NAME);
    iconFavoriteStop = MapIcons('favorite-stop', this.env.THEME_NAME);

    clickedStop: StopMarker | null = null;
    routeClickedStop: StopMarker | null = null;

    exploreMapRoute$ = this.openTripPlannerFacade.exploreMapRoute$;
    routePatternStop$ = this.openTripPlannerFacade.routePatternStop$;
    stops$ = this.openTripPlannerFacade.stops$;
    favoriteStops$ = this.favoritesFacade.favoriteStops$.pipe(
        map((stops) =>
            stops.map((s) => ({
                ...s,
                latlng: { lat: s.lat, lng: s.lon }
            }))
        )
    );
    favoriteLocations$ = this.favoritesFacade.favoriteLocations$;
    banners$ = this.bannersFacade.banners$.pipe(map((banners) => banners.slice(0, 3)));
    allFormattedStops$ = combineLatest([
        this.routeDataService.routeId$,
        this.openTripPlannerFacade.allFormattedStops$,
        this.favoritesFacade.favoriteStops$
    ]).pipe(
        map(([routeId, stops, favoriteStops]) => {
            if (routeId) {
                return [];
            }

            if (!this.env.EXPLORE?.HIDE_FAVORITE_FROM_MAP) {
                return stops.filter((stop) => !favoriteStops.some((fav) => fav.stopId === stop.id));
            }

            return stops;
        })
    );
    allFavoriteStops: StopMarker[] = [];
    routePatternStop: Stop;
    favoriteStops: StopMarker[] = [];
    selectedStop: StopMarker[] = [];
    busLocations$ = this.realtime.vehicleLocations$;
    assetsPath$ = this.applicationFacade.assetsPath$;
    clickedBusData: { tripId?: string; name: string; id: string } | null = null;
    routes: Route[];
    isContrast$ = this.applicationFacade.highContrast$;

    // now it always = 1. If you need other value just change settings
    otpIndex = this.env.OTP_INDEX || 1;
    busLocations = [];
    showedBusLocations = [];
    selectedLocation: FavoriteLocation;
    tripWithBusSelected: boolean;
    allFavoriteLocations: (FavoriteLocation & MarkerItem)[] = [];
    favoriteLocations: (FavoriteLocation & MarkerItem)[] = [];
    selectedFavoriteLocations: (FavoriteLocation & MarkerItem)[] = [];
    selectedStopWithDepartures: { stop: Stop; departures: Departure[] } = { stop: null, departures: [] };
    zoomIconSizeValue: IconSizeEnum;
    zoomValue: number;
    modalBreakpointOffset = 95;
    initialBreakpointIndex = null;
    breakpointIndex = null;
    clickedBus = null;
    clickedBusLocation: MapPoint;
    clickedBusRoute = null;
    lastCheckedLocation: RidLatLng = null;
    hasLocationError = false;
    userDepartureBusStop = null;
    busDataLoading = false;
    deviceStatus = this.network.getCurrentStatus();
    deviceStatus$ = this.network.getStatus();
    componentInited = false;
    fitMap$ = this.mapFitService.fit$;
    userControlledMapPosition = false;
    initialViewSetupComplete = false;

    private currentTrips: TripDetails[] = [];
    private showedBusLocationsSubject = new BehaviorSubject<RealtimeObj[]>([]);
    showedBusLocations$ = this.showedBusLocationsSubject.asObservable();
    private routeId: string | null = null;
    mapIcons: MapIconConfigs;
    // Required for iOS VoiceOver to read map announcements after modal has presented
    didPresentModal: boolean;
    isNativeApp = Capacitor.isNativePlatform();
    private updateOffsetTimeoutId?: ReturnType<typeof setTimeout>;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        @Inject('ENV') public env: Env,
        private renderer: Renderer2,
        private zone: NgZone,
        private bannersFacade: BannersFacade,
        private realtime: RealtimeFacade,
        private headerMenuService: HeaderMenuService,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        private applicationFacade: ApplicationFacade,
        private customerFacade: CustomerFacade,
        private router: Router,
        private route: ActivatedRoute,
        private translate: TranslateService,
        private favoritesFacade: FavoritesFacade,
        private otpService: OpenTripPlannerService,
        private mapApi: MapApiService,
        private authService: AuthService,
        private network: NetworkService,
        private smallScreenSheetContainerService: SmallScreenSheetModalContainerService,
        private routeDataService: RouteDataService,
        private mapFitService: MapFitService
    ) {
        super();
    }

    ngAfterViewInit(): void {
        const currentLocationMarker = document.getElementsByClassName('leaflet-marker-pane')[0];
        currentLocationMarker?.setAttribute('attr.aria-label', this.translate.instant('CURRENT_LOCATION'));
        this.syncMapControlOffsetForBanners();
    }

    ngOnInit(): void {
        if (this.env.OFFLINE_MODE?.CACHE_FAVORITE_SCHEDULES) {
            this.deviceStatus$.pipe(takeUntil(this.componentDestroyed$)).subscribe((status) => {
                this.deviceStatus = status;

                if (this.deviceStatus && !this.componentInited) {
                    this.init();
                }
            });
        }

        if (this.deviceStatus || !this.env.OFFLINE_MODE?.CACHE_FAVORITE_SCHEDULES) {
            this.init();
        }

        if (this.env.SHOW_BANNERS) {
            this.bannersFacade.getBanners();

            this.translate.onLangChange.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
                this.bannersFacade.getBanners();
            });
        }

        if (this.env.EXPLORE?.SHOW_SELECTED_ROUTE_TRANSPORT) {
            this.routeDataService.routeId$
                .pipe(
                    takeUntil(this.componentDestroyed$),
                    distinctUntilChanged(),
                    switchMap((routeId) => {
                        this.routeId = routeId;

                        if (this.routeId) {
                            this.currentTrips = [];
                            this.openTripPlannerFacade.getTripsByRouteId(this.routeId);
                            return this.openTripPlannerFacade.trips$.pipe(take(1));
                        } else {
                            this.currentTrips = [];
                            return of([]);
                        }
                    })
                )
                .subscribe((trips) => {
                    this.currentTrips = trips;
                    this.filterBusesForSelectedRoute();
                });

            this.busLocations$.pipe(takeUntil(this.componentDestroyed$)).subscribe((locations) => {
                this.busLocations = locations;
                this.filterBusesForSelectedRoute();
            });
        }
        if (this.env.EXPLORE?.BREAKPOINT_INDEX != null) {
            this.breakpointIndex = this.env.EXPLORE.BREAKPOINT_INDEX;
        }
    }

    init(): void {
        this.componentInited = true;
        this.openTripPlannerFacade.getAllStops();
        this.openTripPlannerFacade.getAllAlerts();
        this.openTripPlannerFacade.getAllRoutes();
        this.syncFavorites();
        this.renderer.addClass(this.document.body, 'main-full-height');
        if (this.env.EXPLORE?.SHOW_FAVORITE_STOPS) {
            this.favoritesFacade.getFavoriteStops();
        }
        if (this.env.IS_MOBILE_APP) {
            this.scrollToTop();
        }

        this.openTripPlannerFacade.location$.pipe(takeUntil(this.componentDestroyed$)).subscribe((location) => {
            this.hasLocationError = location.hasError;

            if (!location?.coordinates) {
                return;
            }

            if (this.lastCheckedLocation) {
                const coveredDistance = this.calculateDistanceToPreviousPoint(location.coordinates);

                // get the nearest stops only if the distance to the last point is at least 10 meters
                if (coveredDistance <= 10) {
                    return;
                }
            }

            this.lastCheckedLocation = location.coordinates;

            if (!this.router.url.includes('location')) {
                this.openTripPlannerFacade.fetchNearbyStops(
                    location.coordinates.lat,
                    location.coordinates.lng,
                    this.NEARBY_RADIUS_METERS
                );
            }
        });

        this.routePatternStop$.pipe(takeUntil(this.componentDestroyed$)).subscribe((stop) => {
            this.routePatternStop = stop;
            this.setStopMarkers();
        });

        combineLatest([this.allFormattedStops$, this.favoriteStops$])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(([stops, favoriteStops]) => {
                this.allFavoriteStops = stops.filter((s) => !!favoriteStops.find((fs) => fs.stopId === s.id));
                this.setStopMarkers();
            });

        this.openTripPlannerFacade.routes$.pipe(takeUntil(this.componentDestroyed$)).subscribe((routes) => {
            this.routes = routes;
        });

        this.busLocations$.pipe(takeUntil(this.componentDestroyed$)).subscribe((locations) => {
            if (this.clickedBusData && !this.env.EXPLORE?.SELECTED_BUS_STOPPED) {
                return;
            }

            this.busLocations = locations;
            this.showSelectedStopBusses();
        });

        if (this.env.EXPLORE?.SHOW_FAVORITE_LOCATIONS) {
            this.favoritesFacade.getFavoriteLocations();
            this.favoriteLocations$.pipe(takeUntil(this.componentDestroyed$)).subscribe((locations) => {
                this.allFavoriteLocations = locations.map((l) => ({
                    ...l,
                    latlng: new LatLng(l.latitude, l.longitude)
                }));
                this.setFavoriteLocationMarkers();
            });
        }

        this.headerMenuService.openMenu$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
            this.router.navigate([this.translate.currentLang, 'explore']).then(() => this.setStopMarkers());
            this.openTripPlannerFacade.clearExploreMapRoute();
        });

        this.route.queryParams.pipe(takeUntil(this.componentDestroyed$)).subscribe((params) => {
            const url = this.router.createUrlTree([], { relativeTo: this.route }).toString();

            this.setStopMarkers();

            this.tripWithBusSelected = !!params['shortName'];

            if (url.includes('trip')) {
                this.clearUserMapInteractions();
                this.initialViewSetupComplete = false;
            }

            this.selectedLocation = url.includes('location')
                ? {
                      longitude: +params.longitude,
                      latitude: +params.latitude,
                      name: params.name
                  }
                : null;

            this.setFavoriteLocationMarkers();
        });

        this.openTripPlannerFacade.selectedStopWithDepartures$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((selectedStopWithDepartures) => {
                this.selectedStopWithDepartures = selectedStopWithDepartures;
                this.setStopMarkers();
                this.showSelectedStopBusses();
            });

        if (this.authService.getLoginStatus()) {
            this.customerFacade.getCustomerProfile();
            this.actualCustomerLang();
        }

        combineLatest([this.favoritesFacade.favoriteStopsLoading$, this.openTripPlannerFacade.nearbyStopsLoading$])
            .pipe(
                filter(([fLoading, nLoading]) => !fLoading && !nLoading),
                switchMap(() =>
                    combineLatest([
                        this.openTripPlannerFacade.nearbyStops$.pipe(catchError(() => of([]))),
                        this.env.EXPLORE?.SHOW_FAVORITE_STOPS
                            ? this.favoriteStops$.pipe(catchError(() => of([])))
                            : of([]),
                        this.env.EXPLORE?.SHOW_ROUTES
                            ? this.openTripPlannerFacade.routes$.pipe(catchError(() => of([])))
                            : of([]),
                        this.env.EXPLORE?.SHOW_FAVORITE_LOCATIONS ? this.favoriteLocations$ : of([]),
                        this.env.EXPLORE?.SHOW_FAVORITE_SCHEDULE ? this.favoritesFacade.favoriteSchedules$ : of([])
                    ])
                )
            )
            .subscribe(([nearByStops, favoriteStops, routes, favoriteLocations, favoriteSchedules]) => {
                const favoriteElements = [
                    ...(favoriteStops || []),
                    ...(routes || []),
                    ...(favoriteLocations || []),
                    ...(favoriteSchedules || [])
                ];
                const favoriteWithNearByStops = [...(nearByStops || []), ...(favoriteElements || [])];
                this.initialBreakpointIndex = favoriteWithNearByStops.length ? 1 : 0;
                this.smallScreenSheetContainerService.setFavoriteElementsCount(favoriteElements.length);
            });

        this.exploreMapRoute$.pipe(takeUntil(this.componentDestroyed$)).subscribe((route) => {
            this.userDepartureBusStop = route?.userDepartureBusStop
                ? {
                      ...route.userDepartureBusStop,
                      latlng: { lat: route.userDepartureBusStop.lat, lng: route.userDepartureBusStop.lon }
                  }
                : null;

            this.setStopMarkers();
        });
    }

    private resetShowedBusLocations(): void {
        this.showedBusLocationsSubject.next(this.busLocations);
    }

    private filterBusesForSelectedRoute(): void {
        if (!this.routeId || !this.currentTrips.length) {
            this.showedBusLocationsSubject.next(this.busLocations);
            return;
        }

        const tripIds = this.currentTrips.map((trip) =>
            this.env.OTP_INDEX ? trip.id.replace(`${this.env.OTP_INDEX}:`, '') : trip.id
        );

        if (!tripIds.length) {
            this.showedBusLocationsSubject.next([]);
        } else {
            const filteredBuses = this.busLocations.filter((busLocation) => tripIds.includes(busLocation.trip_id));
            this.showedBusLocationsSubject.next(filteredBuses);
        }
    }

    setStopMarkers() {
        const url = this.router.createUrlTree([], { relativeTo: this.route }).toString();
        const isRouteWithoutGroup = url.includes('route') && !url.includes('route-group');
        const isTrip = url.includes('trip');
        const isPatternsWithoutScheduleStop = url.includes('patterns') && !url.includes('schedule/stop');

        if (isRouteWithoutGroup || isTrip || isPatternsWithoutScheduleStop) {
            this.favoriteStops = [];
            this.selectedStop = [];
            return;
        }

        this.allFormattedStops$.pipe(take(1)).subscribe((stops) => {
            this.selectedStop = stops.filter(
                (s) => this.selectedStopWithDepartures?.stop?.id === s.id || this.routePatternStop?.id === s.id
            );
        });

        if (!this.routePatternStop) {
            this.allFormattedStops$.pipe(take(1)).subscribe((stops) => {
                this.favoriteStops = stops.filter(
                    (s) =>
                        this.allFavoriteStops.find((fs) => fs.id === s.id) &&
                        this.selectedStopWithDepartures?.stop?.id !== s.id
                );
            });
        } else {
            this.favoriteStops = [];
        }
    }

    setFavoriteLocationMarkers() {
        this.selectedFavoriteLocations = this.allFavoriteLocations.filter(
            (fl) => fl.latitude === this.selectedLocation?.latitude && fl.longitude === this.selectedLocation?.longitude
        );
        this.favoriteLocations = this.allFavoriteLocations.filter(
            (fl) =>
                !(fl.latitude === this.selectedLocation?.latitude && fl.longitude === this.selectedLocation?.longitude)
        );
    }

    ngOnDestroy(): void {
        this.renderer.removeClass(this.document.body, 'main-full-height');
        this.renderer.removeClass(this.document.body, 'explore-map-with-banners');
        this.document.documentElement.style.removeProperty('--explore-banners-height');
        if (this.updateOffsetTimeoutId) {
            clearTimeout(this.updateOffsetTimeoutId);
        }
        this.openTripPlannerFacade.clearExploreMapRoute();
        this.showedBusLocationsSubject.complete();
        super.ngOnDestroy();

        this.mapApi.removeAllListeners();
    }

    onMapReady() {
        const hasPopupOpenListener = this.mapApi.hasEventListener('popupopen');
        if (!hasPopupOpenListener) {
            this.mapApi.addListenerWithEventCallback('popupopen', (event: PopupEvent) => {
                const popupContent = event.popup.getElement();
                const rmapPopup = popupContent?.querySelector('.rmap-popup > div') as HTMLElement;
                if (!rmapPopup) return;
                setTimeout(() => {
                    //Focus event not occasionally not registered for TalkBack without timeout
                    rmapPopup?.focus();
                }, 100);
            });
        }

        this.attachMapUserInteractionListeners();
    }

    actualCustomerLang(): void {
        const lang = localStorage.getItem('LOCALIZE_DEFAULT_LANGUAGE');
        this.customerFacade.profile$
            .pipe(
                filter((customer) => !ObjectUtil.isObjectEmpty(customer) && customer.lang !== lang),
                take(1)
            )
            .subscribe((customer) => {
                const payload = {
                    name: customer.name,
                    phoneNumber: customer.phone,
                    adr_city: customer.adr_city,
                    adr_street_1: customer.adr_street_1,
                    adr_zip: customer.adr_zip,
                    firstName: customer.firstName,
                    lastName: customer.lastName,
                    phone: {
                        code: customer.phone?.code,
                        number: customer.phone?.number
                    },
                    lang
                };
                localStorage.setItem('LOCALIZE_DEFAULT_LANGUAGE', lang);
                this.customerFacade.saveCustomerProfile(payload);
            });
    }

    onZoomChanged(zoom: number): void {
        this.zoomValue = zoom;
        this.zoomIconSizeValue = IconUtils.getIconSize(zoom);
        this.mapIcons = IconUtils.calculateMapIconsSize(zoom, this.isSelectedStopFavorite());
    }

    isSelectedStopFavorite() {
        return (
            !this.env.EXPLORE.HIDE_FAVORITE_FROM_MAP &&
            !!this.allFavoriteStops?.find((fs) => fs.id === this.selectedStop?.[0]?.id)
        );
    }

    isBusSelected(bus): boolean {
        if (!bus?.trip_id || !this.route.snapshot.queryParams.tripId) {
            return false;
        }
        return this.route.snapshot.queryParams.tripId.includes(bus?.trip_id);
    }

    onStopClick(stop) {
        this.zone.run(() => (this.clickedStop = stop));
    }

    onBusClick(event): void {
        this.clickedBusData = null;
        const bus = event.bus;
        if (this.env.EXPLORE?.SELECTED_BUS_STOPPED) {
            event.event.stopPropagation();
            this.clickedBusRoute = null;
        }

        if (this.zoomIconSizeValue === IconSizeEnum.small) {
            event.event.stopPropagation();

            return;
        }
        if (this.breakpointIndex != null) {
            this.panel.goToBreakpoint(this.breakpointIndex);
        }

        if (!event.bus?.trip_id) {
            const route = this.findRouteForBus(bus);
            if (!route) {
                event.event.stopPropagation();
                return;
            }

            if (this.env.EXPLORE?.SELECTED_BUS_STOPPED) {
                this.handleClickBus(event, route);
                return;
            }

            this.clickedBusData = { name: route.longName, id: route.id };
        } else {
            let tripName: string;
            this.busDataLoading = true;

            if (bus.routeId) {
                this.otpService
                    .getRoute(bus.routeId, null, false)
                    .pipe(takeUntil(this.componentDestroyed$))
                    .subscribe({
                        next: (route) => this.handleBusClickRouteSuccess(event, route, bus, tripName),
                        error: (err) => this.handleBusClickRouteError(event, bus, tripName, bus.trip_id, err)
                    });
                return;
            }

            this.otpService
                .getTripDetails(
                    !this.env.WMB?.DONT_USE_FOR?.includes('REAL_TIME_BUSES')
                        ? bus.trip_id
                        : `${this.otpIndex}:${bus.trip_id}`
                )
                .pipe(
                    takeUntil(this.componentDestroyed$),
                    switchMap((trip) => {
                        if (this.env.EXPLORE?.USE_TRIP_NAME_FOR_DIRECTION_OF_BUS) {
                            tripName = trip.headsign;
                        }

                        return this.otpService.getRoute(trip.routeId, null, false);
                    })
                )
                .subscribe({
                    next: (route) => this.handleBusClickRouteSuccess(event, route, bus, tripName),
                    error: (err) => this.handleBusClickRouteError(event, bus, tripName, null, err)
                });
        }
    }

    private findRouteForBus(bus): Route | undefined {
        return (
            (bus.routeId && this.routes.find((r) => r.id === bus.routeId)) ||
            this.routes.find((r) => r.shortName === bus.route_short_name || r.id === bus.route_short_name)
        );
    }

    private applyBusClickWithRoute(
        event: { bus: { trip_id: string }; event: Event },
        route: Route | DetailedRoute,
        bus: { trip_id: string },
        tripName: string | undefined,
        tripId: string | null
    ): void {
        if (this.env.EXPLORE?.SELECTED_BUS_STOPPED) {
            this.handleClickBus(event, route, tripId, tripName);
        } else {
            this.clickedBusData = tripId
                ? { tripId, name: tripName || route.longName, id: route.id }
                : { name: tripName || route.longName, id: route.id };
        }
    }

    private handleBusClickRouteSuccess(
        event: { bus: { trip_id: string }; event: Event },
        route: Route | DetailedRoute,
        bus: { trip_id: string },
        tripName: string | undefined
    ): void {
        this.busDataLoading = false;
        this.applyBusClickWithRoute(event, route, bus, tripName, bus.trip_id);
    }

    private handleBusClickRouteError(
        event: { bus: { trip_id: string }; event: Event },
        bus: { trip_id: string; routeId?: string; route_short_name: string },
        tripName: string | undefined,
        tripId: string | null,
        err: unknown
    ): void {
        console.error('Failed to fetch route', err);
        this.busDataLoading = false;
        const route = this.findRouteForBus(bus);
        if (!route) {
            event.event.stopPropagation();
            return;
        }
        this.applyBusClickWithRoute(event, route, bus, tripName, tripId);
    }

    handleClickBus(event, route, tripId = null, tripName = null) {
        this.clickedBusRoute = route;
        this.clickedBus = event.bus;

        // After trigger listener "popupclose" to avoid close window
        timer(50)
            .pipe(take(1))
            .subscribe(() => {
                this.clickedBusData = { name: tripName || route.longName, id: route.id, tripId };
                this.clickedBusLocation = { lat: event.bus.latitude, lng: event.bus.longitude, address: null };
            });

        // Timer for center map after open popup
        timer(200)
            .pipe(take(1))
            .subscribe(() => {
                const latLng: RidLatLng = { lat: event.bus.latitude, lng: event.bus.longitude };
                this.mapApi.centerMapToCoordinates(latLng);
            });
    }

    onRouteStopClick(stop) {
        this.zone.run(() => (this.routeClickedStop = stop));
    }

    clickOnStopInfo(stop) {
        if (this.breakpointIndex != null) {
            this.panel.goToBreakpoint(this.breakpointIndex);
        }

        this.router.navigate(['stop', stop.id || stop?.stopId], {
            relativeTo: this.route,
            replaceUrl: this.isStopOrRouteOrLocationSelected()
        });
        this.clickedStop = null;
        this.selectedLocation = null;
        this.setFavoriteLocationMarkers();
        this.mapApi
            .mapReady()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((ready) => {
                if (ready) {
                    const latLng: RidLatLng = { lat: stop.lat, lng: stop.lon };
                    this.mapApi.centerMapToCoordinates(latLng);
                    this.openTripPlannerFacade.setExploreMapStops([stop]);
                }
            });
    }

    clickOnRouteInfo(event) {
        event.stopPropagation();
        event.preventDefault();

        if (!this.clickedBusData?.tripId) {
            this.clickedBusData = null;
            this.mapApi.closePopup();
            return;
        }

        this.router.navigate([`trip`], {
            relativeTo: this.route,
            replaceUrl: this.isStopOrRouteOrLocationSelected(),
            queryParams: {
                tripId: !this.env.WMB?.DONT_USE_FOR?.includes('REAL_TIME_BUSES')
                    ? this.clickedBusData.tripId
                    : `${this.otpIndex}:${this.clickedBusData.tripId}`,
                routeId: this.clickedBusData.id,
                shortName: this.env.EXPLORE?.SELECTED_BUS_STOPPED ? this.clickedBusRoute?.shortName : null
            }
        });
        this.clickedBusData = null;
        this.selectedLocation = null;
        this.mapApi.closePopup();
        this.setFavoriteLocationMarkers();
    }

    clickedFavoriteLocation(location) {
        this.clickedLocation(location.latlng);
    }

    clickedLocation(coordinates): void {
        if (!this.env.EXPLORE?.SHOW_FAVORITE_LOCATIONS) {
            return;
        }

        if (this.clickedStop) {
            this.clickedStop = null;
            return;
        }

        this.selectedLocation = { latitude: coordinates.lat, longitude: coordinates.lng };
        this.setFavoriteLocationMarkers();

        this.router.navigate(['location'], {
            relativeTo: this.route,
            replaceUrl: this.isStopOrRouteOrLocationSelected(),
            queryParams: this.selectedLocation
        });
        this.clickedStop = null;
        this.clickedBusData = null;

        if (this.breakpointIndex != null) {
            this.panel.goToBreakpoint(this.breakpointIndex);
        }
    }

    showSelectedLocation() {
        return (
            this.selectedLocation &&
            !this.allFavoriteLocations.find(
                (l) => l.latitude === this.selectedLocation.latitude && l.longitude === this.selectedLocation.longitude
            )
        );
    }

    /**
     * Prevents Leaflet controls/map UI from overlapping the top banners/notifications on mobile devices.
     *
     * When banners are rendered their container height is measured and propagated to CSS via
     * `--explore-banners-height` and a helper body class. Leaflet styling can then offset controls
     * accordingly so the map doesn't "climb" under the banners.
     */
    private syncMapControlOffsetForBanners(): void {
        if (!this.env.IS_MOBILE_APP || !this.env.SHOW_BANNERS || !this.isNativeApp) {
            return;
        }

        const updateOffset = (): void => {
            const bannersHeight = this.bannersContainer?.nativeElement?.offsetHeight ?? 0;
            const hasBanners = bannersHeight > 0;

            this.renderer[hasBanners ? 'addClass' : 'removeClass'](this.document.body, 'explore-map-with-banners');

            if (hasBanners) {
                this.document.documentElement.style.setProperty('--explore-banners-height', `${bannersHeight}px`);
            } else {
                this.document.documentElement.style.removeProperty('--explore-banners-height');
            }
        };

        this.banners$.pipe(debounceTime(50), takeUntil(this.componentDestroyed$)).subscribe(() => {
            if (this.updateOffsetTimeoutId) {
                clearTimeout(this.updateOffsetTimeoutId);
            }
            this.updateOffsetTimeoutId = setTimeout(updateOffset);
        });

        if (this.updateOffsetTimeoutId) {
            clearTimeout(this.updateOffsetTimeoutId);
        }
        this.updateOffsetTimeoutId = setTimeout(updateOffset);
    }

    private syncFavorites() {
        // we need timeout for sync local favorites data with backend data
        setTimeout(() => {
            this.favoritesFacade.addFavoriteLocationFromLocalstorage(this.allFavoriteLocations);
            this.favoritesFacade.addFavoriteSchedulesFromLocalstorage();
        }, 1000);
    }

    private isStopOrRouteOrLocationSelected() {
        const url = this.router.createUrlTree([], { relativeTo: this.route }).toString();

        return url.includes('explore/stop') || url.includes('explore/trip') || url.includes('explore/location');
    }

    private showSelectedStopBusses(): void {
        if (this.routeId && this.env.EXPLORE?.SHOW_SELECTED_ROUTE_TRANSPORT) {
            return;
        }

        if (this.clickedBusData && this.env.EXPLORE?.SELECTED_BUS_STOPPED) {
            this.showedBusLocationsSubject.next([]);
            return;
        }

        if (this.env.EXPLORE?.SELECTED_BUS_STOPPED && this.route.snapshot.queryParams.tripId) {
            const filteredLocations = this.busLocations.filter(
                (busLocation) => busLocation.trip_id === this.route.snapshot.queryParams.tripId
            );

            this.showedBusLocationsSubject.next(filteredLocations);

            if (this.env.EXPLORE?.BUS_AUTO_CENTERING) {
                this.centerMapToBusLocation(filteredLocations);
            }

            return;
        }

        if (!this.selectedStopWithDepartures?.stop) {
            this.showedBusLocationsSubject.next(this.busLocations);
        } else {
            const filteredLocations = this.busLocations.filter(
                (busLocation) =>
                    !!this.selectedStopWithDepartures.departures.find(
                        (d) =>
                            (busLocation.routeId && d.route.id === busLocation.routeId) ||
                            d.route.shortName === busLocation.route_short_name ||
                            d.route.id === busLocation.route_short_name
                    )
            );
            this.showedBusLocationsSubject.next(filteredLocations);
        }
    }

    private calculateDistanceToPreviousPoint(coordinates) {
        return getDistance(
            {
                latitude: this.lastCheckedLocation.lat,
                longitude: this.lastCheckedLocation.lng
            },
            {
                latitude: coordinates.lat,
                longitude: coordinates.lng
            }
        );
    }

    private scrollToTop(): void {
        this.document.querySelector('ion-content')?.scrollToTop().then();
        window.scrollTo(0, 0);
    }

    onFullscreenChanged($event: boolean) {
        // Hide leaflet controls from screen reader when fullscreen is active
        // When true, the modal is overlaying the map
        if ($event) {
            const controls = document.querySelectorAll('.rounded-controls');
            controls.forEach((el: Element) => {
                el.setAttribute('aria-hidden', 'true');
            });
        } else {
            const controls = document.querySelectorAll('.rounded-controls');
            controls.forEach((el: Element) => {
                el.removeAttribute('aria-hidden');
            });
        }
    }

    private centerMapToBusLocation(locations: RealtimeObj[]): void {
        if (!locations.length || this.userControlledMapPosition) {
            return;
        }

        const latLng: RidLatLng = {
            lat: locations[0].latitude,
            lng: locations[0].longitude
        };

        if (!this.initialViewSetupComplete) {
            this.mapApi.once('moveend', () => {
                this.clearUserMapInteractions();
                this.initialViewSetupComplete = true;
            });
        }

        this.mapApi.centerMapToCoordinates(latLng, 16, {
            animate: true,
            duration: 1.5,
            easeLinearity: 1.5,
            noMoveStart: true
        });
    }

    private attachMapUserInteractionListeners(): void {
        this.mapApi.addListener('drag', () => {
            if (this.initialViewSetupComplete) {
                this.userControlledMapPosition = true;
            }
        });

        this.mapApi.addListener('zoom', () => {
            if (this.initialViewSetupComplete) {
                this.userControlledMapPosition = true;
            }
        });

        if (this.env.EXPLORE?.SELECTED_BUS_STOPPED) {
            this.mapApi.addListener('popupclose', () => {
                if (this.clickedBusData) {
                    this.clickedBusData = null;
                }
            });
        }
    }

    private clearUserMapInteractions() {
        this.userControlledMapPosition = false;
    }
}
